import { useState } from 'react';
import { ALLOWED_EXAM_TAGS, EXAM_TAG_LABEL, type ExamTag } from '../lib/examTags';
import {
  ALL_MODES, QUALIFICATION_OPTIONS,
  type Mode, type MentorFilterState, type MentorCategory, type Offering,
} from '../lib/mentorFilterState';

// Filter panel for the live-mentors directory. Renders inline (left column) on wide
// screens and collapses behind a "Filters" toggle on narrow ones. State-driven: the
// parent owns MentorFilterState (synced to the URL) and gets every change via onChange.
export function MentorFilters({ state, onChange, cityOptions, hobbyOptions, sportOptions, categoryOptions }: { state: MentorFilterState; onChange: (next: MentorFilterState) => void; cityOptions?: { value: string; label: string; count: number }[]; hobbyOptions?: { key: string; label: string; count?: number }[]; sportOptions?: { key: string; label: string; count?: number }[]; categoryOptions?: { key: string; label: string; count?: number }[] }) {
  const [open, setOpen] = useState(false);

  const toggleArr = <T,>(arr: T[] | undefined, v: T): T[] => {
    const set = new Set(arr ?? []);
    set.has(v) ? set.delete(v) : set.add(v);
    return [...set];
  };
  const setOrUndef = <T,>(arr: T[]): T[] | undefined => (arr.length ? arr : undefined);

  // Category dropdown value: an 'off:*' offering wins over a stored category.
  const catValue = state.offering ? `off:${state.offering}` : (state.category ?? '');
  const setCat = (v: string) => {
    if (v.startsWith('off:')) onChange({ ...state, category: undefined, offering: v.slice(4) as Offering });
    else onChange({ ...state, offering: undefined, category: (v || undefined) as MentorCategory | undefined });
  };

  const body = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
      {/* Category — options come from Home (admin-configurable label/order/visibility); the
          'all' entry is rendered as "Any category". */}
      {(categoryOptions?.length ?? 0) > 0 && (
        <Group label="Category">
          <select value={catValue} onChange={(e) => setCat(e.target.value)} style={Select}>
            <option value="">Any category</option>
            {categoryOptions!.filter((o) => o.key !== 'all').map((o) => (
              <option key={o.key} value={o.key}>{o.label}{o.count ? ` (${o.count})` : ''}</option>
            ))}
          </select>
        </Group>
      )}

      {/* City options come ONLY from the cities real mentors actually saved (with counts),
          so the option string always equals a stored value → exact match never misses.
          No fixed-metro fallback: a curated "Delhi" never matched a mentor's "New Delhi". */}
      {(cityOptions?.length ?? 0) > 0 && (
        <Group label="City">
          <select value={state.city ?? ''} onChange={(e) => onChange({ ...state, city: e.target.value || undefined })} style={Select}>
            <option value="">Any city</option>
            {cityOptions!.map((c) => <option key={c.value} value={c.value}>{c.label}{c.count ? ` (${c.count})` : ''}</option>)}
          </select>
        </Group>
      )}

      {/* Hobbies dropdown — options are the hobbies real mentors saved (with counts). */}
      {(hobbyOptions?.filter((h) => h.key !== 'all').length ?? 0) > 0 && (
        <Group label="Hobby">
          <select value={(state.hobbies && state.hobbies[0]) ?? ''} onChange={(e) => onChange({ ...state, hobbies: e.target.value ? [e.target.value] : undefined })} style={Select}>
            <option value="">Any hobby</option>
            {hobbyOptions!.filter((h) => h.key !== 'all').map((h) => <option key={h.key} value={h.key}>{h.label}{h.count ? ` (${h.count})` : ''}</option>)}
          </select>
        </Group>
      )}

      {/* Games dropdown — options are the sports real mentors saved (with counts). */}
      {(sportOptions?.filter((s) => s.key !== 'all').length ?? 0) > 0 && (
        <Group label="Game">
          <select value={(state.sports && state.sports[0]) ?? ''} onChange={(e) => onChange({ ...state, sports: e.target.value ? [e.target.value] : undefined })} style={Select}>
            <option value="">Any game</option>
            {sportOptions!.filter((s) => s.key !== 'all').map((s) => <option key={s.key} value={s.key}>{s.label}{s.count ? ` (${s.count})` : ''}</option>)}
          </select>
        </Group>
      )}

      <Group label="Exams (any of)">
        <div style={ChipWrap}>
          {ALLOWED_EXAM_TAGS.map((tag) => (
            <Toggle key={tag} on={(state.exams ?? []).includes(tag)}
              onClick={() => onChange({ ...state, exams: setOrUndef(toggleArr<ExamTag>(state.exams, tag)) })}>
              {EXAM_TAG_LABEL[tag]}
            </Toggle>
          ))}
        </div>
      </Group>

      <Group label="Mode (any of)">
        <div style={ChipWrap}>
          {ALL_MODES.map((m) => (
            <Toggle key={m} on={(state.modes ?? []).includes(m)}
              onClick={() => onChange({ ...state, modes: setOrUndef(toggleArr<Mode>(state.modes, m)) })}>
              {m === 'in-person' ? 'In-person' : m[0]!.toUpperCase() + m.slice(1)}
            </Toggle>
          ))}
        </div>
      </Group>

      <Group label="Minimum experience">
        <select value={state.minYearsExperience ?? ''} onChange={(e) => onChange({ ...state, minYearsExperience: e.target.value ? Number(e.target.value) : undefined })} style={Select}>
          <option value="">Any</option>
          {[1, 3, 5, 10, 15].map((y) => <option key={y} value={y}>{y}+ years</option>)}
        </select>
      </Group>

      <Group label="Minimum rating">
        <select value={state.minRating ?? ''} onChange={(e) => onChange({ ...state, minRating: e.target.value ? Number(e.target.value) : undefined })} style={Select}>
          <option value="">Any</option>
          {[4, 4.5, 5].map((r) => <option key={r} value={r}>{r}★ &amp; up</option>)}
        </select>
      </Group>

      <Group label="Qualifications (any of)">
        <div style={ChipWrap}>
          {QUALIFICATION_OPTIONS.map((q) => (
            <Toggle key={q} on={(state.qualifications ?? []).includes(q)}
              onClick={() => onChange({ ...state, qualifications: setOrUndef(toggleArr<string>(state.qualifications, q)) })}>
              {q}
            </Toggle>
          ))}
        </div>
      </Group>

      <Group label="Consultation">
        <select value={state.consultation ?? ''} onChange={(e) => onChange({ ...state, consultation: (e.target.value || undefined) as MentorFilterState['consultation'] })} style={Select}>
          <option value="">Any</option>
          <option value="free">🆓 Offers free consultation</option>
          <option value="paid">💼 Offers paid sessions</option>
          <option value="both">🆓💼 Both available</option>
        </select>
      </Group>

      <Group label="Type">
        <div style={ChipWrap}>
          <Toggle on={state.counsellor === true}
            onClick={() => onChange({ ...state, counsellor: state.counsellor ? undefined : true })}>
            🧭 Counsellors
          </Toggle>
        </div>
      </Group>
    </div>
  );

  return (
    <aside style={{ width: '100%' }}>
      <button type="button" onClick={() => setOpen((o) => !o)} style={ToggleBtn}>
        {open ? '▾' : '▸'} Filters
      </button>
      {/* Always visible on wide screens via CSS? Repo is inline-style-only, so we render
          the panel when toggled open; the toggle is shown on every width. */}
      {open && <div style={{ marginTop: '0.75rem' }}>{body}</div>}
    </aside>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
      {children}
    </div>
  );
}

function Toggle({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '0.3rem 0.6rem', borderRadius: 999, fontSize: '0.78rem', cursor: 'pointer',
      border: `1px solid ${on ? '#1a4480' : '#cbd5e1'}`, background: on ? '#1a4480' : 'white', color: on ? 'white' : '#475569', fontWeight: 600,
    }}>{children}</button>
  );
}

const Select: React.CSSProperties = { width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.88rem', background: 'white' };
const ChipWrap: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '0.4rem' };
const ToggleBtn: React.CSSProperties = { fontSize: '0.9rem', fontWeight: 700, color: '#1a4480', background: 'white', border: '1px solid #cbd5e1', borderRadius: 8, padding: '0.5rem 0.9rem', cursor: 'pointer' };
